import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Badge,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Input,
  MoneyInput,
  RichTextEditor,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Textarea,
} from '@mlm/ui';
import { Decimal, merchantEarning, resolveMerchantLine } from '@mlm/shared';
import {
  apiErrorMessage,
  createProduct,
  deleteProductImage,
  getMerchantMe,
  getPricingDefaults,
  getProductVariations,
  listCategories,
  listProducts,
  listVariationTypes,
  productImageUrl,
  setProductVariations,
  setStock,
  setVariantStock,
  updateProduct,
  updateVariant,
  uploadProductImage,
  type MerchantPricingDefaults,
  type MerchantProduct,
  type MerchantVariant,
  type ProductVariationSelection,
} from '../lib/api';

const statusVariant: Record<string, 'success' | 'secondary' | 'destructive'> = {
  approved: 'success',
  pending: 'secondary',
  rejected: 'destructive',
};

export function ProductsPage() {
  const qc = useQueryClient();
  const { data: me } = useQuery({ queryKey: ['merchant-me'], queryFn: getMerchantMe });
  const approved = me?.merchant.status === 'approved';
  const { data: products = [], isLoading } = useQuery({
    queryKey: ['merchant-products'],
    queryFn: listProducts,
    enabled: approved,
  });
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<MerchantProduct | null>(null);
  // Prefer the freshly-fetched list row (so the dialog reflects saves) but fall
  // back to the captured object if a refetch is mid-flight.
  const editingProduct = editing ? (products.find((p) => p.id === editing.id) ?? editing) : null;

  // Pending / rejected / suspended merchants cannot submit products — the API
  // rejects them with 403 `merchant_not_approved`. Mirror that gate in the UI so
  // the submission form is never reachable (including via direct URL).
  if (me && !approved) {
    return (
      <div className="max-w-4xl space-y-4">
        <h1 className="text-2xl font-semibold">Products</h1>
        <Card>
          <CardContent className="py-10 text-center text-muted-foreground">
            Your merchant account is <span className="font-medium capitalize">{me.merchant.status}</span>.
            You can submit products for review once an admin approves your account.
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-4xl space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Products</h1>
        <Button onClick={() => setCreating(true)}>New product</Button>
      </div>

      {isLoading ? (
        <p className="text-muted-foreground">Loading…</p>
      ) : products.length === 0 ? (
        <Card><CardContent className="py-10 text-center text-muted-foreground">No products yet. Add your first.</CardContent></Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead className="border-b text-left text-muted-foreground">
                <tr>
                  <th className="p-3">Name</th>
                  <th className="p-3">Price</th>
                  <th className="p-3">Fee %</th>
                  <th className="p-3">Receivable Wallet</th>
                  <th className="p-3">Stock</th>
                  <th className="p-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {products.map((p) => (
                  <ProductRow
                    key={p.id}
                    product={p}
                    onStock={() => qc.invalidateQueries({ queryKey: ['merchant-products'] })}
                    onEdit={() => setEditing(p)}
                  />
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {creating && <MerchantProductDialog mode="create" onClose={() => setCreating(false)} />}
      {editingProduct && (
        <MerchantProductDialog
          key={editingProduct.id}
          mode="edit"
          product={editingProduct}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

function ProductRow({
  product,
  onStock,
  onEdit,
}: {
  product: MerchantProduct;
  onStock: () => void;
  onEdit: () => void;
}) {
  const [qty, setQty] = useState(String(product.stockQty ?? 0));
  const [variationsOpen, setVariationsOpen] = useState(false);
  const mut = useMutation({
    meta: { successMessage: 'Stock updated.' },
    mutationFn: (n: number) => setStock(product.id, n),
    onSuccess: () => {
      onStock();
    },
  });
  const variants = product.variants ?? [];
  const hasVariants = variants.length > 0;
  return (
    <tr className="border-b last:border-0 hover:bg-muted/30 align-top">
      <td className="p-3">
        <div className="font-medium">{product.name}</div>
        <div className="text-xs text-muted-foreground">{product.sku}</div>
        {product.rejectionReason && <div className="text-xs text-destructive">{product.rejectionReason}</div>}
        <div className="mt-1 flex gap-3">
          <button type="button" className="text-xs text-primary hover:underline" onClick={onEdit}>
            Edit
          </button>
          <button
            type="button"
            className="text-xs text-primary hover:underline"
            onClick={() => setVariationsOpen(true)}
          >
            Variations…
          </button>
        </div>
        <VariationsDialog
          productId={product.id}
          productName={product.name}
          open={variationsOpen}
          onOpenChange={setVariationsOpen}
          onChanged={onStock}
        />
      </td>
      <td className="p-3 tabular-nums">₱{Number(product.price).toFixed(2)}</td>
      <td className="p-3">{product.platformFeePct ? `${Number(product.platformFeePct)}%` : '—'}</td>
      <td className="p-3 tabular-nums">
        ₱{merchantEarning(new Decimal(product.price), product.platformFeePct ?? 0).toFixed(2)}
      </td>
      <td className="p-3">
        {hasVariants ? (
          <div className="space-y-1.5">
            {variants.map((v) => (
              <VariantStockRow
                key={v.id}
                productId={product.id}
                variant={v}
                feePct={product.platformFeePct}
                onStock={onStock}
              />
            ))}
          </div>
        ) : (
          <div className="flex items-center gap-1">
            <MoneyInput integer className="w-20 h-8" value={qty} onChange={(e) => setQty(e.target.value)} />
            <Button size="sm" variant="outline" disabled={mut.isPending} onClick={() => mut.mutate(Number(qty))}>
              Save
            </Button>
          </div>
        )}
      </td>
      <td className="p-3">
        <Badge variant={statusVariant[product.approvalStatus] ?? 'secondary'} className="capitalize">
          {product.approvalStatus}
        </Badge>
      </td>
    </tr>
  );
}

function VariantStockRow({
  productId,
  variant,
  feePct,
  onStock,
}: {
  productId: string;
  variant: MerchantVariant;
  // Variants inherit the product's platform fee — there is no per-variant fee.
  feePct: string | null;
  onStock: () => void;
}) {
  const [qty, setQty] = useState(String(variant.stockQty ?? 0));
  const mut = useMutation({
    meta: { successMessage: 'Stock updated.' },
    mutationFn: (n: number) => setVariantStock(productId, variant.id, n),
    onSuccess: () => {
      onStock();
    },
  });
  return (
    <div className="flex items-center gap-1.5">
      <span className="w-28 truncate text-xs" title={variant.name}>
        {variant.name}
      </span>
      <span className="w-16 text-xs tabular-nums text-muted-foreground" title="Price">
        ₱{Number(variant.price).toFixed(2)}
      </span>
      <span
        className="w-20 text-xs tabular-nums text-emerald-600"
        title="Receivable to your wallet (price − platform fee)"
      >
        → ₱{merchantEarning(new Decimal(variant.price), feePct ?? 0).toFixed(2)}
      </span>
      <MoneyInput integer className="w-16 h-8" value={qty} onChange={(e) => setQty(e.target.value)} />
      <Button size="sm" variant="outline" disabled={mut.isPending} onClick={() => mut.mutate(Number(qty))}>
        Save
      </Button>
    </div>
  );
}

/**
 * Thin dialog wrapper around the matrix editor for the ProductRow "Variations…"
 * quick link. The editor itself is shared with the product dialog's Variations
 * tab. Mounting the editor only while `open` resets its local axes selection on
 * close.
 */
function VariationsDialog({
  productId,
  productName,
  open,
  onOpenChange,
  onChanged,
}: {
  productId: string;
  productName: string;
  open: boolean;
  onOpenChange: (o: boolean) => void;
  onChanged: () => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Variations — {productName}</DialogTitle>
        </DialogHeader>
        {open && <MerchantVariationsEditor productId={productId} onChanged={onChanged} />}
      </DialogContent>
    </Dialog>
  );
}

/**
 * Variation matrix editor for an existing merchant product. Pick the global
 * Size/Color axes + values → generate a variant per combination. Per-variant you
 * can set price/SRP/stock and (under ⚙) weight + L/W/H. Saving re-pends the
 * product for admin review. No PV / binary points / vortex (house-only).
 */
/** One row's editable draft, lifted into the editor for a single bulk save. */
type VariantDraft = {
  sku: string;
  price: string;
  srp: string;
  stockQty: string;
  active: boolean;
  weightKg: string;
  lengthCm: string;
  widthCm: string;
  heightCm: string;
};

const ovNum = (v: string | null | undefined) => (v == null ? '' : String(Number(v)));
function draftFromVariant(v: MerchantVariant): VariantDraft {
  return {
    sku: v.sku,
    price: String(Number(v.price)),
    srp: v.srp ? String(Number(v.srp)) : '',
    stockQty: String(v.stockQty ?? 0),
    active: v.active,
    weightKg: ovNum(v.weightKg),
    lengthCm: ovNum(v.lengthCm),
    widthCm: ovNum(v.widthCm),
    heightCm: ovNum(v.heightCm),
  };
}

function MerchantVariationsEditor({
  productId,
  onChanged,
}: {
  productId: string;
  onChanged: () => void;
}) {
  const qc = useQueryClient();
  const typesQ = useQuery({ queryKey: ['merchant-variation-types'], queryFn: () => listVariationTypes() });
  const configQ = useQuery({
    queryKey: ['merchant-product-variations', productId],
    queryFn: () => getProductVariations(productId),
  });
  const [sel, setSel] = useState<Record<string, string[]> | null>(null);

  const types = typesQ.data ?? [];
  const serverSel = configQ.data?.selections;
  if (sel === null && serverSel) {
    const seed: Record<string, string[]> = {};
    for (const s of serverSel) seed[s.typeId] = s.optionIds;
    setSel(seed);
  }
  const current = sel ?? {};
  const variants = configQ.data?.variants ?? [];

  // Lifted per-row drafts so the whole matrix saves with one button. Re-seed
  // whenever the variant set changes (e.g. after Generate matrix adds/removes
  // rows); editing in place leaves the signature unchanged so drafts persist.
  const [rows, setRows] = useState<Record<string, VariantDraft>>({});
  const seededSigRef = useRef<string>('');
  const sig = variants.map((v) => v.id).join(',');
  useEffect(() => {
    if (seededSigRef.current === sig) return;
    const next: Record<string, VariantDraft> = {};
    for (const v of variants) next[v.id] = draftFromVariant(v);
    setRows(next);
    seededSigRef.current = sig;
  }, [sig]); // eslint-disable-line react-hooks/exhaustive-deps

  const patchRow = (id: string, patch: Partial<VariantDraft>) =>
    setRows((prev) => {
      const base = prev[id];
      if (!base) return prev;
      return { ...prev, [id]: { ...base, ...patch } };
    });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['merchant-product-variations', productId] });
    qc.invalidateQueries({ queryKey: ['merchant-products'] });
    onChanged();
  };
  const generate = useMutation({
    meta: { successMessage: 'Variations saved — product re-submitted for review.' },
    mutationFn: () => {
      const selections: ProductVariationSelection[] = Object.entries(current)
        .filter(([, ids]) => ids.length > 0)
        .map(([typeId, optionIds]) => ({ typeId, optionIds }));
      return setProductVariations(productId, selections);
    },
    onSuccess: invalidate,
  });

  // Single bulk save: push every CHANGED row in one go (price/SRP/active/dims
  // via updateVariant, stock via setVariantStock), then invalidate once.
  const orNull = (s: string) => (s.trim() === '' ? null : s.trim());
  const saveAll = useMutation({
    meta: { successMessage: 'Variations saved — product re-submitted for review.' },
    mutationFn: async () => {
      await Promise.all(
        variants.map(async (v) => {
          const d = rows[v.id];
          if (!d) return;
          const server = draftFromVariant(v);
          const fieldsChanged =
            d.sku !== server.sku ||
            d.price !== server.price ||
            d.srp !== server.srp ||
            d.active !== server.active ||
            d.weightKg !== server.weightKg ||
            d.lengthCm !== server.lengthCm ||
            d.widthCm !== server.widthCm ||
            d.heightCm !== server.heightCm;
          if (fieldsChanged) {
            await updateVariant(productId, v.id, {
              sku: d.sku.trim().toUpperCase(),
              price: d.price,
              srp: d.srp || null,
              active: d.active,
              weightKg: orNull(d.weightKg),
              lengthCm: orNull(d.lengthCm),
              widthCm: orNull(d.widthCm),
              heightCm: orNull(d.heightCm),
            });
          }
          if (d.stockQty !== server.stockQty) {
            await setVariantStock(productId, v.id, Number(d.stockQty) || 0);
          }
        }),
      );
    },
    onSuccess: invalidate,
  });

  function toggleType(typeId: string, on: boolean) {
    setSel((prev) => {
      const next = { ...(prev ?? {}) };
      if (on) next[typeId] = next[typeId] ?? [];
      else delete next[typeId];
      return next;
    });
  }
  function toggleOption(typeId: string, optionId: string, on: boolean) {
    setSel((prev) => {
      const next = { ...(prev ?? {}) };
      const set = new Set(next[typeId] ?? []);
      if (on) set.add(optionId);
      else set.delete(optionId);
      next[typeId] = [...set];
      return next;
    });
  }

  if (typesQ.isLoading || configQ.isLoading) {
    return <p className="text-sm text-muted-foreground">Loading…</p>;
  }
  if (types.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        No variation types are available yet. Ask the store admin to add Size / Color options.
      </p>
    );
  }

  return (
    <div className="space-y-4">
      <div className="space-y-3 rounded-md border bg-muted/20 p-3">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Variation axes</p>
        {types.map((t) => {
          const used = t.id in current;
          return (
            <div key={t.id} className="space-y-1">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={used} onChange={(e) => toggleType(t.id, e.target.checked)} />
                <span className="font-medium">{t.name}</span>
              </label>
              {used && (
                <div className="ml-6 flex flex-wrap gap-1.5">
                  {t.options.length === 0 && (
                    <span className="text-xs text-muted-foreground">No values for this type.</span>
                  )}
                  {t.options.map((o) => {
                    const on = (current[t.id] ?? []).includes(o.id);
                    return (
                      <button
                        key={o.id}
                        type="button"
                        onClick={() => toggleOption(t.id, o.id, !on)}
                        className={`rounded-full border px-2.5 py-0.5 text-xs ${
                          on ? 'border-primary bg-primary/10 font-medium' : 'text-muted-foreground'
                        }`}
                      >
                        {o.label}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
        <div className="flex items-center gap-2 pt-1">
          <Button type="button" size="sm" disabled={generate.isPending} onClick={() => generate.mutate()}>
            {generate.isPending ? 'Generating…' : 'Generate matrix'}
          </Button>
          <span className="text-[11px] text-muted-foreground">Saving re-submits the product for review.</span>
        </div>
      </div>

      {variants.length > 0 && (
        <div className="space-y-2">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-[11px] text-muted-foreground">
              Edit price, SRP, visibility and stock, then save the whole matrix at once.
            </p>
            <Button
              type="button"
              size="sm"
              disabled={saveAll.isPending || generate.isPending}
              onClick={() => saveAll.mutate()}
            >
              {saveAll.isPending ? 'Saving…' : 'Save all variations'}
            </Button>
          </div>
          <table className="w-full text-sm">
            <thead className="text-left text-muted-foreground">
              <tr>
                <th className="py-1 pr-2">Variation</th>
                <th className="py-1 pr-2">Price</th>
                <th className="py-1 pr-2">SRP</th>
                <th className="py-1 pr-2">Active</th>
                <th className="py-1 pr-2">Stock</th>
              </tr>
            </thead>
            <tbody>
              {variants.map((v) => {
                const draft = rows[v.id] ?? draftFromVariant(v);
                return (
                  <MerchantVariantRow
                    key={v.id}
                    variant={v}
                    draft={draft}
                    onChange={(patch) => patchRow(v.id, patch)}
                  />
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function MerchantVariantRow({
  variant,
  draft,
  onChange,
}: {
  variant: MerchantVariant;
  draft: VariantDraft;
  onChange: (patch: Partial<VariantDraft>) => void;
}) {
  const [adv, setAdv] = useState(false);

  return (
    <>
      <tr className="border-t align-top">
        <td className="py-1 pr-2">
          <div className="flex items-center gap-1">
            <button
              type="button"
              className={`text-xs ${adv ? 'text-foreground' : 'text-muted-foreground'} hover:text-foreground`}
              title="Weight & dimensions"
              onClick={() => setAdv((v) => !v)}
            >
              {adv ? '▾' : '⚙'}
            </button>
            <span>
              {variant.name}
              <Input
                aria-label={`SKU for ${variant.name}`}
                className="mt-0.5 h-6 w-36 font-mono text-xs"
                value={draft.sku}
                onChange={(e) => onChange({ sku: e.target.value.toUpperCase() })}
              />
            </span>
          </div>
        </td>
        <td className="py-1 pr-2"><MoneyInput className="w-20 h-8" value={draft.price} onChange={(e) => onChange({ price: e.target.value })} /></td>
        <td className="py-1 pr-2"><MoneyInput className="w-20 h-8" value={draft.srp} onChange={(e) => onChange({ srp: e.target.value })} /></td>
        <td className="py-1 pr-2">
          <Button type="button" size="sm" variant={draft.active ? 'outline' : 'ghost'} onClick={() => onChange({ active: !draft.active })}>
            {draft.active ? 'Active' : 'Hidden'}
          </Button>
        </td>
        <td className="py-1 pr-2"><MoneyInput integer className="w-16 h-8" value={draft.stockQty} onChange={(e) => onChange({ stockQty: e.target.value })} /></td>
      </tr>
      {adv && (
        <tr className="bg-muted/20">
          <td colSpan={5} className="px-3 py-2">
            <p className="mb-1 text-[10px] uppercase tracking-wide text-muted-foreground">
              Per-variation shipping — blank = inherit product
            </p>
            <div className="flex flex-wrap gap-3">
              <MerchantAdvField label="Weight (g)" value={draft.weightKg} onChange={(v) => onChange({ weightKg: v })} />
              <MerchantAdvField label="Length (cm)" value={draft.lengthCm} onChange={(v) => onChange({ lengthCm: v })} />
              <MerchantAdvField label="Width (cm)" value={draft.widthCm} onChange={(v) => onChange({ widthCm: v })} />
              <MerchantAdvField label="Height (cm)" value={draft.heightCm} onChange={(v) => onChange({ heightCm: v })} />
              <span className="self-end text-[11px] text-muted-foreground">Use “Save all variations” to apply.</span>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function MerchantAdvField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="space-y-1">
      <label className="text-[10px] text-muted-foreground">{label}</label>
      <MoneyInput className="h-8 w-24" placeholder="inherit" value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

/**
 * Unified create/edit product dialog with tabs (mirrors the admin product
 * dialog). On CREATE the product doesn't exist until the info tab is saved, so
 * the Images / Details / Variations tabs are disabled until then; saving creates
 * the product (held in `created`) and advances to the Images tab. Each non-info
 * tab saves independently. Merchant products are always sold on the storefront
 * to everyone — there are no sale-channel / buyer-eligibility selectors.
 */
function MerchantProductDialog({
  mode,
  product,
  onClose,
}: {
  mode: 'create' | 'edit';
  product?: MerchantProduct;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const { data: defaults } = useQuery({ queryKey: ['merchant-pricing-defaults'], queryFn: getPricingDefaults });

  // In create mode the product is persisted on first info-save and held here so
  // the remaining tabs (which key off its id) can be enabled.
  const [created, setCreated] = useState<MerchantProduct | null>(null);
  const current = product ?? created;
  const [tab, setTab] = useState('info');
  // Rich Details HTML is lifted here so it survives tab switches (Radix unmounts
  // inactive tab bodies) and is submitted together with the info form on create.
  const [detailsHtml, setDetailsHtml] = useState(product?.detailsHtml ?? '');

  const create = useMutation({
    meta: { successMessage: 'Product created — sent to the admin for review. Now add images.' },
    mutationFn: (body: Record<string, unknown>) => createProduct(body) as Promise<MerchantProduct>,
    onSuccess: (p) => {
      qc.invalidateQueries({ queryKey: ['merchant-products'] });
      setCreated(p);
      setTab('images');
    },
  });
  const update = useMutation({
    meta: { successMessage: 'Changes saved — sent to the admin for review.' },
    mutationFn: (body: Record<string, unknown>) => updateProduct(current!.id, body) as Promise<MerchantProduct>,
    onSuccess: (p) => {
      qc.invalidateQueries({ queryKey: ['merchant-products'] });
      if (created) setCreated(p);
    },
  });
  const saveDetails = useMutation({
    meta: { successMessage: 'Details saved.' },
    mutationFn: () =>
      updateProduct(current!.id, { detailsHtml: detailsHtml.trim() === '' ? null : detailsHtml }) as Promise<MerchantProduct>,
    onSuccess: (p) => {
      qc.invalidateQueries({ queryKey: ['merchant-products'] });
      if (created) setCreated(p);
    },
  });

  // Every tab body shares one fixed height + internal scroll so switching tabs
  // doesn't resize the dialog (the info form is much taller than the others).
  const tabBody = 'h-[34rem] overflow-y-auto pr-1';
  const infoSubmitting = create.isPending || update.isPending;
  const infoSubmitLabel = !current
    ? infoSubmitting
      ? 'Saving…'
      : 'Save & continue →'
    : infoSubmitting
      ? 'Saving…'
      : 'Save';

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{mode === 'create' ? 'New product' : `Edit ${product?.name ?? 'product'}`}</DialogTitle>
          <DialogDescription>
            {mode === 'create' && !current
              ? 'Enter the product details to create it, then add images, details and variations. Merchant products sell on the storefront to everyone.'
              : 'Each tab saves independently. Merchant products sell on the storefront to everyone.'}
          </DialogDescription>
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab} className="mt-1">
          <TabsList className="flex-wrap">
            <TabsTrigger value="info">Product info</TabsTrigger>
            <TabsTrigger value="images" disabled={!current}>Images</TabsTrigger>
            <TabsTrigger value="details" disabled={!current}>Details</TabsTrigger>
            <TabsTrigger value="variations" disabled={!current}>Variations</TabsTrigger>
          </TabsList>

          <TabsContent value="info" className={tabBody}>
            <MerchantProductDetailsForm
              product={current ?? undefined}
              defaults={defaults}
              submitLabel={infoSubmitLabel}
              submitting={infoSubmitting}
              saved={(update.isSuccess || create.isSuccess) && !infoSubmitting}
              error={(create.error ?? update.error) as unknown}
              // Edit mode ALWAYS updates; create mode creates first, then updates
              // the just-created row. Never key this on object truthiness alone.
              onSubmit={(body) => {
                const payload = { ...body, detailsHtml: detailsHtml.trim() === '' ? null : detailsHtml };
                if (mode === 'edit' || current) update.mutate(payload);
                else create.mutate(payload);
              }}
              onCancel={onClose}
            />
          </TabsContent>

          <TabsContent value="images" className={tabBody}>
            {created && (
              <p className="mb-2 text-sm text-success">
                Product created — add at least one image so it shows well in the store.
              </p>
            )}
            {current && <MerchantImagesSection productId={current.id} initial={current.images ?? []} />}
          </TabsContent>

          <TabsContent value="details" className={`${tabBody} space-y-3`}>
            <p className="text-sm text-muted-foreground">
              Full product details shown on the storefront product page. Use the toolbar to format — no
              code needed.
            </p>
            <RichTextEditor
              value={detailsHtml}
              onChange={setDetailsHtml}
              placeholder="Describe the product — ingredients, usage, what's in the box…"
            />
            {current ? (
              <div className="flex items-center justify-end gap-3">
                {saveDetails.isSuccess && <span className="text-xs text-success">Saved.</span>}
                <Button type="button" onClick={() => saveDetails.mutate()} disabled={saveDetails.isPending}>
                  {saveDetails.isPending ? 'Saving…' : 'Save details'}
                </Button>
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">Saved together when you create the product.</p>
            )}
          </TabsContent>

          <TabsContent value="variations" className={tabBody}>
            {current && (
              <MerchantVariationsEditor
                productId={current.id}
                onChanged={() => qc.invalidateQueries({ queryKey: ['merchant-products'] })}
              />
            )}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}

type InfoBody = {
  name: string;
  /** Your own catalog code. Blank on create → we generate one for you. */
  sku?: string;
  description?: string;
  price: string;
  srp?: string;
  categoryId?: string | null;
  stockQty?: number;
  weightKg?: string;
  lengthCm?: string;
  widthCm?: string;
  heightCm?: string;
};

function MerchantProductDetailsForm({
  product,
  defaults,
  submitLabel,
  submitting,
  saved,
  error,
  onSubmit,
  onCancel,
}: {
  product?: MerchantProduct;
  defaults?: MerchantPricingDefaults;
  submitLabel: string;
  submitting: boolean;
  saved?: boolean;
  error: unknown;
  onSubmit: (body: InfoBody) => void;
  onCancel: () => void;
}) {
  const ov = (v: string | null | undefined) => (v == null || v === '' ? '' : String(Number(v)));
  const [name, setName] = useState(product?.name ?? '');
  const [sku, setSku] = useState(product?.sku ?? '');
  const [description, setDescription] = useState(product?.description ?? '');
  const [price, setPrice] = useState(product ? String(Number(product.price)) : '');
  const [srp, setSrp] = useState(product?.srp ? String(Number(product.srp)) : '');
  const [stockQty, setStockQty] = useState('');
  const [weightKg, setWeightKg] = useState(ov(product?.weightKg));
  const [lengthCm, setLengthCm] = useState(ov(product?.lengthCm));
  const [widthCm, setWidthCm] = useState(ov(product?.widthCm));
  const [heightCm, setHeightCm] = useState(ov(product?.heightCm));
  const [categoryId, setCategoryId] = useState<string>(product?.categoryId ?? '');
  const { data: categories = [] } = useQuery({ queryKey: ['categories'], queryFn: listCategories });

  // Couriers quote on dimensions — all four are required and must be > 0.
  const dimsOk = [weightKg, lengthCm, widthCm, heightCm].every((v) => Number(v) > 0);

  // First unmet requirement, surfaced next to the Save button so a disabled
  // button is never a mystery.
  const disabledReason = !name.trim()
    ? 'Enter a product name to save'
    : !price.trim()
      ? 'Enter a selling price to save'
      : !dimsOk
        ? 'Add weight & dimensions to save'
        : null;

  // Once the product exists, stock is managed per-product (or per-variant) in the
  // list; the Initial-stock field only seeds a single-SKU product at creation.
  const isCreate = !product;

  function submit(e: React.FormEvent) {
    e.preventDefault();
    const body: InfoBody = {
      name: name.trim(),
      // Blank on create → the server generates one. On edit an unchanged value
      // is still sent; the server no-ops when it matches.
      sku: sku.trim() ? sku.trim().toUpperCase() : undefined,
      description: description.trim() || undefined,
      price: price.trim(),
      srp: srp.trim() || undefined,
      weightKg: weightKg.trim() || undefined,
      lengthCm: lengthCm.trim() || undefined,
      widthCm: widthCm.trim() || undefined,
      heightCm: heightCm.trim() || undefined,
      categoryId: categoryId === '' ? null : categoryId,
    };
    if (isCreate) body.stockQty = stockQty.trim() ? Number(stockQty) : 0;
    onSubmit(body);
  }

  return (
    <form className="space-y-3" onSubmit={submit}>
      <Field label="Name" value={name} onChange={(e) => setName(e.target.value)} />
      <div className="space-y-1">
        <Field
          label={isCreate ? 'SKU (optional)' : 'SKU'}
          value={sku}
          onChange={(e) => setSku(e.target.value.toUpperCase())}
        />
        <p className="text-xs text-muted-foreground">
          {isCreate
            ? 'Your own catalog code. Leave blank and we\u2019ll generate one.'
            : 'Your own catalog code. Editing it re-submits the listing for review.'}
        </p>
      </div>
      <div className="space-y-1">
        <label className="text-sm font-medium">Short description</label>
        <Textarea value={description} onChange={(e) => setDescription(e.target.value)} />
      </div>
      <div className="space-y-1">
        <label className="text-sm font-medium">Category</label>
        <select
          value={categoryId}
          onChange={(e) => setCategoryId(e.target.value)}
          className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          <option value="">— Uncategorized —</option>
          {categories.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
        <p className="text-xs text-muted-foreground">
          Categories are defined by the admin and group your product in the store
          filters.
        </p>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="SRP — slashed price (₱)" money value={srp} onChange={(e) => setSrp(e.target.value)} />
        <Field label="Selling price (₱)" money value={price} onChange={(e) => setPrice(e.target.value)} />
        {isCreate && <Field label="Initial stock" money integer value={stockQty} onChange={(e) => setStockQty(e.target.value)} />}
        <Field label="Weight (kg) *" money value={weightKg} onChange={(e) => setWeightKg(e.target.value)} />
        <Field label="Length (cm) *" money value={lengthCm} onChange={(e) => setLengthCm(e.target.value)} />
        <Field label="Width (cm) *" money value={widthCm} onChange={(e) => setWidthCm(e.target.value)} />
        <Field label="Height (cm) *" money value={heightCm} onChange={(e) => setHeightCm(e.target.value)} />
      </div>

      {/* Earnings preview — only when admin enabled it for this merchant. */}
      {price && defaults?.showFeeToMerchant && (
        <EarningsPreview srp={srp} price={price} defaults={defaults} />
      )}

      {!isCreate && (
        <p className="text-xs text-muted-foreground">
          Stock is managed from the products list (per-variation for variant products).
        </p>
      )}
      <p className="text-xs text-muted-foreground">
        Saving sends the product to the admin for review before it appears in the store.
      </p>
      {error ? (
        <p className="text-sm text-destructive">
          {apiErrorMessage(error, 'Could not save the product. Check the fields.')}
        </p>
      ) : null}
      {!dimsOk && (
        <p className="text-xs text-muted-foreground">
          Weight and all dimensions (L × W × H) are required and must be greater than 0.
        </p>
      )}
      <div className="flex items-center gap-3">
        <Button type="submit" disabled={submitting || !name.trim() || !price.trim() || !dimsOk}>
          {submitLabel}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        {/* Tell the merchant exactly what's blocking the save, then confirm success. */}
        {!submitting && disabledReason ? (
          <span className="text-sm text-muted-foreground">{disabledReason}</span>
        ) : saved && !error ? (
          <span className="text-sm text-success">✓ Saved.</span>
        ) : null}
      </div>
    </form>
  );
}

/** Product image gallery — upload + delete. Mirrors the admin ImagesSection. */
function MerchantImagesSection({ productId, initial }: { productId: string; initial: string[] }) {
  const [images, setImages] = useState<string[]>(initial);
  const upload = useMutation({
    meta: { successMessage: 'Image uploaded.' },
    mutationFn: (file: File) => uploadProductImage(productId, file),
    onSuccess: (r) => setImages(r.images),
  });
  const remove = useMutation({
    meta: { successMessage: 'Image removed.' },
    mutationFn: (key: string) => deleteProductImage(productId, key),
    onSuccess: (r) => setImages(r.images),
  });
  const busy = upload.isPending || remove.isPending;

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2">
        {images.map((key, idx) => (
          <div key={key} className="relative h-20 w-20 rounded-md border overflow-hidden group">
            <img
              src={`${productImageUrl(productId, idx)}?v=${encodeURIComponent(key)}`}
              alt=""
              className="h-full w-full object-cover"
            />
            <button
              type="button"
              disabled={busy}
              onClick={() => remove.mutate(key)}
              className="absolute top-0.5 right-0.5 h-5 w-5 rounded-full bg-background/80 text-destructive text-xs leading-none hover:bg-background"
              title="Remove"
            >
              ✕
            </button>
          </div>
        ))}
        <label className="h-20 w-20 rounded-md border border-dashed flex items-center justify-center text-xs text-muted-foreground cursor-pointer hover:bg-accent">
          {upload.isPending ? 'Uploading…' : '+ Image'}
          <input
            type="file"
            accept="image/*"
            className="hidden"
            disabled={busy}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) upload.mutate(f);
              e.target.value = '';
            }}
          />
        </label>
      </div>
      {(upload.error || remove.error) && (
        <p className="text-xs text-destructive">Image operation failed (check file type/size).</p>
      )}
    </div>
  );
}

function Field(props: {
  label: string;
  value?: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  money?: boolean;
  integer?: boolean;
}) {
  return (
    <div className="space-y-1">
      <label className="text-sm font-medium">{props.label}</label>
      {props.money ? (
        <MoneyInput value={props.value ?? ''} onChange={props.onChange} integer={props.integer} />
      ) : (
        <Input value={props.value ?? ''} onChange={props.onChange} />
      )}
    </div>
  );
}

/**
 * SRP → "you receive" waterfall for the add-product dialog, shown only when the
 * admin enabled `showFeeToMerchant`. Uses the merchant's admin-set platform fee
 * % + VAT. PV is deliberately NOT shown — it's an admin-only concern finalized
 * at approval.
 */
function EarningsPreview({
  srp,
  price,
  defaults,
}: {
  srp: string;
  price: string;
  defaults: MerchantPricingDefaults;
}) {
  const sell = new Decimal(price || 0);
  // pvCommissionPct is irrelevant here (PV hidden from merchants); pass 0.
  const { commission, gross, net } = resolveMerchantLine(sell, {
    platformFeePct: defaults.platformFeePct,
    pvCommissionPct: '0',
    vatEnabled: defaults.vatEnabled,
    vatPct: defaults.vatPct,
    // RTU is admin-configured at approval; irrelevant to this merchant preview.
    rtuCeilingPct: null,
    rtuLevelShares: null,
  });
  const srpD = srp ? new Decimal(srp) : null;
  const discount = srpD && srpD.gt(sell) ? srpD.minus(sell) : null;
  const row = (label: string, value: string, strong = false) => (
    <div className={`flex justify-between ${strong ? 'font-semibold' : ''}`}>
      <span>{label}</span>
      <span className="tabular-nums">{value}</span>
    </div>
  );
  return (
    <div className="space-y-1 rounded-md border bg-muted/30 p-3 text-xs">
      {srpD && row('SRP (slashed)', `₱${srpD.toFixed(2)}`)}
      {discount && row('− Discount', `₱${discount.toFixed(2)}`)}
      {row('Selling price (members pay)', `₱${sell.toFixed(2)}`, true)}
      {row(`− Platform fee ${Number(defaults.platformFeePct)}%`, `₱${commission.toFixed(2)}`)}
      {row('Merchant gross', `₱${gross.toFixed(2)}`)}
      {defaults.vatEnabled && row(`− VAT ${Number(defaults.vatPct)}%`, `₱${gross.minus(net).toFixed(2)}`)}
      {row('You receive', `₱${net.toFixed(2)}`, true)}
      <div className="pt-1 text-muted-foreground">Estimated — admin finalizes the rate at approval.</div>
    </div>
  );
}
